export const GOOGLE_ADS_CONVERSION_ID = 'AW-1032475049';
export const GOOGLE_ADS_VERIFIED_SIGNUP_SEND_TO = `${GOOGLE_ADS_CONVERSION_ID}/eiYoCLiCv8gcEKmjqewD`;
export const GOOGLE_ADS_VERIFIED_SIGNUP_PENDING_KEY = 'trinidad_google_ads_verified_signup_pending';
export const GOOGLE_ADS_VERIFIED_SIGNUP_SENT_KEY =
  'trinidad_google_ads_verified_signup_conversion_sent';

type GoogleAdsGtag = (
  command: 'event',
  action: 'conversion',
  params: {
    currency: 'USD';
    send_to: typeof GOOGLE_ADS_VERIFIED_SIGNUP_SEND_TO;
    value: 1.0;
  },
) => void;

type ConversionStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

declare global {
  interface Window {
    gtag?: GoogleAdsGtag;
  }
}

function getStorage(storage: ConversionStorage | undefined, kind: 'local' | 'session') {
  if (storage || typeof window === 'undefined') {
    return storage ?? null;
  }

  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function getItem(storage: ConversionStorage | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function setItem(storage: ConversionStorage | null, key: string, value: string) {
  try {
    storage?.setItem(key, value);
  } catch {
    // Conversion tracking should never block signup or verification.
  }
}

function removeItem(storage: ConversionStorage | null, key: string) {
  try {
    storage?.removeItem(key);
  } catch {
    // Conversion tracking should never block signup or verification.
  }
}

function isVerifySuccessHash(hash: string): boolean {
  const params = new URLSearchParams(hash.replace(/^#/, ''));

  return Boolean(
    params.get('access_token')?.trim() &&
      params.get('refresh_token')?.trim() &&
      params.get('type') === 'magiclink',
  );
}

export function markGoogleAdsVerifiedSignupPending({
  storage,
}: { storage?: ConversionStorage } = {}) {
  setItem(getStorage(storage, 'local'), GOOGLE_ADS_VERIFIED_SIGNUP_PENDING_KEY, '1');
}

export function fireGoogleAdsVerifiedSignupConversionOnce({
  gtag,
  hash,
  pendingStorage,
  sentStorage,
}: {
  gtag?: GoogleAdsGtag;
  hash?: string;
  pendingStorage?: ConversionStorage;
  sentStorage?: ConversionStorage;
} = {}): boolean {
  const verifyHash = hash ?? (typeof window === 'undefined' ? '' : window.location.hash);

  if (!isVerifySuccessHash(verifyHash)) {
    return false;
  }

  const localStorage = getStorage(pendingStorage, 'local');
  const sessionStorage = getStorage(sentStorage, 'session');

  if (getItem(localStorage, GOOGLE_ADS_VERIFIED_SIGNUP_PENDING_KEY) !== '1') {
    return false;
  }

  if (getItem(sessionStorage, GOOGLE_ADS_VERIFIED_SIGNUP_SENT_KEY) === '1') {
    return false;
  }

  setItem(sessionStorage, GOOGLE_ADS_VERIFIED_SIGNUP_SENT_KEY, '1');
  removeItem(localStorage, GOOGLE_ADS_VERIFIED_SIGNUP_PENDING_KEY);

  const conversionGtag = gtag ?? (typeof window === 'undefined' ? undefined : window.gtag);

  if (!conversionGtag) {
    return false;
  }

  try {
    conversionGtag('event', 'conversion', {
      currency: 'USD',
      send_to: GOOGLE_ADS_VERIFIED_SIGNUP_SEND_TO,
      value: 1.0,
    });
  } catch {
    return false;
  }

  return true;
}
